import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { calculateSquadScore, calculatePeriodSquadScore } from "../../lib/squad-score";
import { useWorkspaceData } from "../../lib/workspace-data";
import { useSeason } from "../../lib/seasons";
import { EmptyState, SectionLoading } from "../../components/state";
import { RankMedal } from "../../components/primitives";
import { StatStrip } from "./StatStrip";
import { Sparkline } from "../../components/charts";
import type { MatchPlayerStat, Player, Session, SessionResponse, SquadScoreSetting } from "../../types";
import { useTranslation } from "react-i18next";
import {
  type StatRow,
  type SquadScorePeriod,
  squadScorePeriodBounds,
  squadScorePeriodLabel,
  SQUAD_SCORE_PERIODS,
  matchesPlayedByPlayer,
} from "./stats-utils";

function RankingTable({
  rows,
  onSelect,
}: {
  rows: StatRow[];
  onSelect: (row: StatRow) => void;
}) {
  const { t } = useTranslation("stats");
  if (rows.length === 0) return <EmptyState text={t("players.ranking.empty")} />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left eb-data text-[13px] py-2 pr-2 w-8">#</th>
            <th className="text-left eb-data text-[13px] py-2 pr-2">{t("players.ranking.thPlayer")}</th>
            <th className="text-left eb-data text-[13px] px-1 hidden sm:table-cell">{t("players.ranking.thRole")}</th>
            <th className="text-right eb-data text-[13px] px-1">{t("players.ranking.thEvents")}</th>
            <th className="text-right eb-data text-[13px] px-1">{t("players.ranking.thG")}</th>
            <th className="text-right eb-data text-[13px] px-1 hidden md:table-cell">{t("players.ranking.thA")}</th>
            <th className="text-right eb-data text-[13px] px-1 hidden md:table-cell">{t("players.ranking.thT")}</th>
            <th className="text-right eb-data text-[13px] px-1 hidden md:table-cell">{t("players.ranking.thPct")}</th>
            <th className="text-right eb-data text-[13px] px-1" title="Squad Score">{t("players.ranking.thSS")}</th>
            <th className="text-left eb-data text-[13px] px-1 hidden lg:table-cell">{t("players.ranking.thForm")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const formData = [...row.matchRows]
              .sort((a, b) => String(a.match_starts_at || "").localeCompare(String(b.match_starts_at || "")))
              .slice(-12)
              .map((s) => (s.goals || 0) + (s.assists || 0) * 0.75 + (s.started ? 0.5 : 0));
            while (formData.length < 12) formData.unshift(0);
            return (
              <tr
                key={row.player.id}
                onClick={() => onSelect(row)}
                className="border-t hover:bg-raised cursor-pointer"
              >
                <td className="py-2 pr-2 eb-num text-[20px] text-[hsl(var(--faint))]">{idx + 1}</td>
                <td className="py-2 pr-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="min-w-0">
                      <p className="font-medium truncate text-sm leading-tight">{row.player.display_name}</p>
                      <p className="text-xs text-muted-foreground font-mono leading-tight">
                        #{row.player.shirt_number ?? "-"} · {row.player.ea_sports_id || t("players.ranking.eaNa")}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-1 hidden sm:table-cell"><span className="tag-mono">{row.player.role_code || "-"}</span></td>
                <td className="text-right font-tabular px-1">{row.responseTotal}</td>
                <td className="text-right font-tabular px-1 font-semibold">{row.goals}</td>
                <td className="text-right font-tabular px-1 hidden md:table-cell">{row.assists}</td>
                <td className="text-right font-tabular px-1 hidden md:table-cell">{row.starts}</td>
                <td className="text-right font-tabular px-1 hidden md:table-cell text-muted-foreground">{row.presenceRate}%</td>
                <td className="text-right font-tabular px-1 font-semibold tabular-nums">{row.score.toFixed(1)}</td>
                <td className="px-1 hidden lg:table-cell">
                  <Sparkline values={formData} color="hsl(var(--accent))" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LeaderBoard({
  columns,
  onSelect,
}: {
  columns: Array<{ key: string; title: string; rows: StatRow[]; metric: "goals" | "assists" | "starts" }>;
  onSelect: (row: StatRow) => void;
}) {
  const { t } = useTranslation("stats");
  const [active, setActive] = useState(columns[0].key);
  return (
    <section className="rounded-[20px] bg-card p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[17px] font-semibold">{t("players.leader.title")}</h2>
        <span className="text-[13px] text-[hsl(var(--faint))]">{t("players.leader.top")}</span>
      </div>
      <div role="tablist" className="lg:hidden mt-3 grid grid-cols-3 rounded-full bg-raised p-1 text-sm">
        {columns.map((column) => (
          <button
            key={column.key}
            type="button"
            role="tab"
            aria-selected={active === column.key}
            onClick={() => setActive(column.key)}
            className={cn("h-9 rounded-full font-medium truncate px-2", active === column.key ? "bg-[hsl(var(--active))] text-foreground" : "text-muted-foreground")}
          >
            {column.title}
          </button>
        ))}
      </div>
      <div className="mt-2 grid lg:grid-cols-3 lg:gap-8">
        {columns.map((column) => (
          <div key={column.key} className={cn("min-w-0", active === column.key ? "block" : "hidden", "lg:block")}>
            <p className="hidden lg:block mb-1 text-sm text-muted-foreground">{column.title}</p>
            {column.rows.map((row, idx) => (
              <button
                key={row.player.id}
                className="w-full flex items-center gap-3 py-2.5 border-t first:border-t-0 lg:border-t-0 text-left"
                onClick={() => onSelect(row)}
              >
                <RankMedal rank={idx + 1} />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium truncate">{row.player.display_name}</span>
                  <span className="block text-[13px] text-[hsl(var(--faint))] truncate">{row.player.role_code || t("players.leader.roleNa")}</span>
                </span>
                <span className={cn("eb-num text-[26px] pr-1", idx === 0 && row[column.metric] > 0 && "text-[hsl(var(--ok))]")}>{row[column.metric]}</span>
              </button>
            ))}
            {column.rows.length === 0 && <EmptyState text={t("players.leader.empty")} />}
          </div>
        ))}
      </div>
    </section>
  );
}

function SquadScoreLeaderboard({
  players,
  responses,
  matchStats,
  events,
  setting,
  referenceNow,
  onOpenPlayer,
}: {
  players: Player[];
  responses: SessionResponse[];
  matchStats: MatchPlayerStat[];
  events: Session[];
  setting: SquadScoreSetting | null | undefined;
  referenceNow?: number;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("stats");
  const [period, setPeriod] = useState<SquadScorePeriod>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const bounds = squadScorePeriodBounds(period, customFrom, customTo, referenceNow);
  const sessionsInPeriod = events.filter((e) => {
    const t = new Date(e.starts_at).getTime();
    if (Number.isNaN(t)) return false;
    if (bounds.fromMs !== null && t < bounds.fromMs) return false;
    if (bounds.toMs !== null && t > bounds.toMs) return false;
    return true;
  }).length;

  const rows = setting
    ? players
        .map((player) => {
          const playerResponses = responses.filter((r) => r.player_id === player.id);
          const playerStats = matchStats.filter((s) => s.player_id === player.id);
          const { score, eventsCount, avgPerEvent } = calculatePeriodSquadScore(
            playerResponses,
            playerStats,
            setting,
            { fromMs: bounds.fromMs, toMs: bounds.toMs },
            sessionsInPeriod,
            referenceNow,
          );
          return { player, score, events: eventsCount, avg: avgPerEvent };
        })
        .filter((row) => row.score !== 0 || row.events > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10)
    : [];

  const effectiveMin = setting ? Math.min(setting.min_events, sessionsInPeriod || setting.min_events) : 0;

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            {t("players.ss.title")}
          </CardTitle>
          <CardDescription>
            {setting
              ? t("players.ss.presetActive", { label: bounds.label, name: setting.name })
              : t("players.ss.noPreset")}
          </CardDescription>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          <Select value={period} onValueChange={(v) => setPeriod(v as SquadScorePeriod)}>
            <SelectTrigger className="w-[160px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SQUAD_SCORE_PERIODS.map((k) => (
                <SelectItem key={k} value={k}>{squadScorePeriodLabel(k)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {period === "custom" && (
            <>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-8 w-[140px] text-xs"
              />
              <span className="text-xs text-muted-foreground">→</span>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-8 w-[140px] text-xs"
              />
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {!setting && <EmptyState text={t("players.ss.configPrompt")} />}
        {setting && rows.length === 0 && <EmptyState text={t("players.ss.emptyPeriod")} />}
        {rows.map((row, idx) => (
          <button
            key={row.player.id}
            onClick={() => onOpenPlayer(row.player.id)}
            className="w-full flex items-center justify-between rounded-xl px-3 py-2 hover:bg-muted/60 transition-smooth text-left"
          >
            <div className="flex items-center gap-3 min-w-0">
              <RankMedal rank={idx + 1} />
              <div className="min-w-0">
                <p className="font-medium truncate">{row.player.display_name}</p>
                <p className="text-xs text-muted-foreground">
                  {t("players.ss.responses", { count: row.events })}
                  {effectiveMin > 0 && row.events < effectiveMin
                    ? t("players.ss.belowThreshold", { min: effectiveMin })
                    : ""}
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="eb-num text-[28px] pr-1">{row.score.toFixed(1)}</span>
              {row.events > 0 && (
                <p className="text-xs text-muted-foreground tabular-nums">
                  {t("players.ss.avgPerEvent", { value: row.avg.toFixed(2) })}
                </p>
              )}
            </div>
          </button>
        ))}
      </CardContent>
    </Card>
  );
}

export function PlayersStatsTab({
  teamId,
  onOpenPlayer,
  allSeasons = false,
  referenceNow,
  competitionId = null,
}: {
  teamId: string;
  onOpenPlayer: (playerId: string) => void;
  allSeasons?: boolean;
  referenceNow?: number;
  competitionId?: string | null;
}) {
  const { t } = useTranslation("stats");
  const { t: tSeasons } = useTranslation("seasons");
  const { selectedSeason } = useSeason();
  const { players, events, attendance: responses, matches, matchStats, isLoading } = useWorkspaceData(teamId, { allSeasons, competitionId });
  const activeSettingQuery = useQuery({
    queryKey: ["squad-score-settings", teamId, "active"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("squad_score_settings")
        .select("*")
        .eq("team_id", teamId)
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data || null) as SquadScoreSetting | null;
    },
    staleTime: 60_000,
  });
  const activeSetting = activeSettingQuery.data;
  const [sortMetric, setSortMetric] = useState<"score" | "goals" | "assists" | "presence" | "starts" | "name">("goals");
  const [searchTerm, setSearchTerm] = useState("");

  const rows = useMemo(() => {
    const filteredResponses = responses;
    const played = matchesPlayedByPlayer(matches, responses, matchStats);

    const sortedRows = players.map((player): StatRow => {
      const playerResponses = filteredResponses.filter((r) => r.player_id === player.id);
      const playerStats = matchStats.filter((s) => s.player_id === player.id);
      const present = playerResponses.filter((r) => r.status === "present").length;
      const late = playerResponses.filter((r) => r.status === "late").length;
      const absent = playerResponses.filter((r) => r.status === "absent").length;
      const noResponse = playerResponses.filter((r) => r.status === "no_response").length;
      const responseTotal = present + late + absent;
      const presenceRate = responseTotal > 0 ? Math.round(((present + late) / responseTotal) * 100) : 0;
      const goals = playerStats.reduce((acc, s) => acc + (s.goals || 0), 0);
      const assists = playerStats.reduce((acc, s) => acc + (s.assists || 0), 0);
      const starts = playerStats.filter((s) => s.started).length;
      const yellowCards = playerStats.reduce((acc, s) => acc + (s.yellow_cards || 0), 0);
      const redCards = playerStats.reduce((acc, s) => acc + (s.red_cards || 0), 0);
      const score = activeSetting
        ? calculateSquadScore(playerResponses, playerStats, activeSetting, referenceNow)
        : 0;
      const matchRows = [...playerStats].sort((a, b) =>
        String(b.match_starts_at || "").localeCompare(String(a.match_starts_at || "")),
      );
      return { player, present, late, absent, noResponse, responseTotal, presenceRate, goals, assists, starts, yellowCards, redCards, score, matchesPlayed: played.get(player.id) || 0, matchRows };
    });

    return sortedRows.sort((a, b) => {
      if (sortMetric === "name") return a.player.display_name.localeCompare(b.player.display_name);
      if (sortMetric === "goals") return b.goals - a.goals || b.assists - a.assists;
      if (sortMetric === "assists") return b.assists - a.assists || b.goals - a.goals;
      if (sortMetric === "presence") return b.presenceRate - a.presenceRate || b.responseTotal - a.responseTotal;
      if (sortMetric === "starts") return b.starts - a.starts || b.goals - a.goals;
      return b.score - a.score;
    });
  }, [players, responses, matches, matchStats, activeSetting, sortMetric, referenceNow]);

  const rosterAllRows = rows.filter((r) => !r.player.is_guest);
  const visibleRows = rows.filter((r) => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return true;
    return `${r.player.display_name} ${r.player.role_code || ""}`.toLowerCase().includes(needle);
  });
  const rosterRows = visibleRows.filter((r) => !r.player.is_guest);
  const topGoals = [...rosterAllRows].sort((a, b) => b.goals - a.goals).slice(0, 5);
  const topAssists = [...rosterAllRows].sort((a, b) => b.assists - a.assists).slice(0, 5);
  const topStarts = [...rosterAllRows].sort((a, b) => b.starts - a.starts).slice(0, 5);
  const totals = rosterAllRows.reduce(
    (acc, r) => ({
      goals: acc.goals + r.goals,
      assists: acc.assists + r.assists,
      present: acc.present + r.present,
      late: acc.late + r.late,
      absent: acc.absent + r.absent,
      noResponse: acc.noResponse + r.noResponse,
      starts: acc.starts + r.starts,
      yellowCards: acc.yellowCards + r.yellowCards,
      redCards: acc.redCards + r.redCards,
    }),
    { goals: 0, assists: 0, present: 0, late: 0, absent: 0, noResponse: 0, starts: 0, yellowCards: 0, redCards: 0 },
  );

  if (isLoading) return <SectionLoading />;

  return (
    <div className="space-y-6">
      <section className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {allSeasons || !selectedSeason ? t("players.filters.allSeasons") : tSeasons("scope.season", { name: selectedSeason.name })} · {t("players.filters.visibleRows", { count: visibleRows.length })}
        </p>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 sm:flex-none sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("players.filters.search")}
              className="pl-9"
            />
          </div>
          <Select value={sortMetric} onValueChange={(value) => setSortMetric(value as typeof sortMetric)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="goals">{t("players.filters.sortGoals")}</SelectItem>
              <SelectItem value="assists">{t("players.filters.sortAssists")}</SelectItem>
              <SelectItem value="starts">{t("players.filters.sortStarts")}</SelectItem>
              <SelectItem value="presence">{t("players.filters.sortPresence")}</SelectItem>
              <SelectItem value="score">{t("players.filters.sortScore")}</SelectItem>
              <SelectItem value="name">{t("players.filters.sortName")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <StatStrip
        items={[
          { label: t("players.metric.teamGoals"), value: totals.goals },
          { label: t("players.metric.teamAssists"), value: totals.assists },
          { label: t("players.metric.rosterPlayers"), value: rosterAllRows.length },
          { label: t("players.metric.yellowCards"), value: totals.yellowCards },
          { label: t("players.metric.redCards"), value: totals.redCards },
          { label: t("players.metric.noResponses"), value: totals.noResponse },
        ]}
      />

      <LeaderBoard
        columns={[
          { key: "goals", title: t("players.ranking2.sortGoals"), rows: topGoals, metric: "goals" },
          { key: "assists", title: t("players.ranking2.sortAssists"), rows: topAssists, metric: "assists" },
          { key: "starts", title: t("players.ranking2.sortStarts"), rows: topStarts, metric: "starts" },
        ]}
        onSelect={(row) => onOpenPlayer(row.player.id)}
      />

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-lg">
              {t("players.ranking2.titlePrefix")} · {
                sortMetric === "goals" ? t("players.ranking2.sortGoals") :
                sortMetric === "assists" ? t("players.ranking2.sortAssists") :
                sortMetric === "presence" ? t("players.ranking2.sortPresence") :
                sortMetric === "starts" ? t("players.ranking2.sortStarts") :
                t("players.ranking2.sortName")
              }
            </CardTitle>
            <CardDescription>{t("players.ranking2.subtitle", { count: rosterRows.length })}</CardDescription>
          </div>
          <div className="inline-flex max-w-full overflow-x-auto rounded-full bg-raised p-1 text-sm">
            {(["goals", "assists", "starts", "presence"] as const).map((k) => (
              <button
                key={k}
                onClick={() => setSortMetric(k)}
                className={cn(
                  "px-3.5 py-1.5 rounded-full whitespace-nowrap font-medium transition-colors",
                  sortMetric === k ? "bg-[hsl(var(--active))] text-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {k === "goals" ? t("players.ranking2.sortGoals") : k === "assists" ? t("players.ranking2.sortAssists") : k === "starts" ? t("players.ranking2.sortStarts") : t("players.ranking2.sortPresence")}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <RankingTable rows={rosterRows} onSelect={(row) => onOpenPlayer(row.player.id)} />
        </CardContent>
      </Card>

      <SquadScoreLeaderboard
        players={players.filter((p) => !p.is_guest)}
        responses={responses}
        matchStats={matchStats}
        events={events}
        setting={activeSetting}
        referenceNow={referenceNow}
        onOpenPlayer={onOpenPlayer}
      />

    </div>
  );
}
